import { useMemo } from 'react'
import type { CSSProperties } from 'react'
import { useSales } from '../../hooks/useSales'
import { useExpenses } from '../../hooks/useExpenses'
import { usePrints } from '../../hooks/usePrints'
import { Card } from '../../components/ui'
import { formatMoney } from '../../lib/format'
import { computeProfitForPeriod, saleNetRevenue } from '../../lib/profit'
import {
  type TimeRange,
  timeRangeDays,
  timeRangeLabel,
  isWithinRange,
  bucketShape,
} from '../../lib/timeRange'

interface StatsGridProps {
  range: TimeRange
}

export default function StatsGrid({ range }: StatsGridProps) {
  const { data: sales } = useSales()
  const { data: expenses } = useExpenses()
  const { data: prints } = usePrints()

  const stats = useMemo(() => {
    const rangeSales = (sales ?? []).filter((s) => isWithinRange(s.sale_date, range))
    const rangeExpenses = (expenses ?? []).filter((e) => isWithinRange(e.expense_date, range))
    const rangePrints = (prints ?? []).filter((p) => isWithinRange(p.print_date, range))

    const breakdown = computeProfitForPeriod({
      sales: rangeSales,
      expenses: rangeExpenses,
      prints: rangePrints,
    })

    const totalHours = rangePrints.reduce((sum, p) => sum + Number(p.print_time_hours), 0)

    return {
      income: breakdown.income,
      expenseTotal: breakdown.expensesOut,
      profit: breakdown.profit,
      totalHours,
    }
  }, [sales, expenses, prints, range])

  // Sparkline: bucket sales into N bars matching the range's shape.
  const sparkline = useMemo(() => {
    const { bucketDays, numBuckets } = bucketShape(range)
    const totalDays = timeRangeDays(range)
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    const buckets = new Array(numBuckets).fill(0)
    for (const s of sales ?? []) {
      // Refunded sales must not contribute to the sparkline — otherwise the
      // bar chart disagrees with the income tile sitting right above it,
      // which sums via computeProfitForPeriod (already excludes refunds).
      if (s.is_refunded) continue
      const saleDate = new Date(s.sale_date)
      saleDate.setHours(0, 0, 0, 0)
      const daysAgo = Math.floor((now.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24))
      if (daysAgo < 0 || daysAgo >= totalDays) continue
      // Bucket 0 is oldest in the window; last bucket = today
      const bucketIdx = numBuckets - 1 - Math.floor(daysAgo / bucketDays)
      if (bucketIdx >= 0 && bucketIdx < numBuckets) {
        buckets[bucketIdx] += saleNetRevenue(s)
      }
    }
    return buckets
  }, [sales, range])

  const maxBar = Math.max(1, ...sparkline)
  const totalSold = sparkline.reduce((a, b) => a + b, 0)
  const avgPerDay = totalSold / timeRangeDays(range)
  const rangeLabel = timeRangeLabel(range)

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div style={gridStyle}>
        <Stat label={`Income (${rangeLabel})`} value={formatMoney(stats.income)} tone="success" />
        <Stat label={`Expenses (${rangeLabel})`} value={formatMoney(-stats.expenseTotal)} tone="danger" />
        <Stat
          label={`Profit (${rangeLabel})`}
          value={formatMoney(stats.profit)}
          tone={stats.profit >= 0 ? 'success' : 'danger'}
        />
        <Stat label={`Printer hours (${rangeLabel})`} value={`${stats.totalHours.toFixed(0)}h`} tone="default" />
      </div>
      <Card padding="var(--space-3)" style={{ marginTop: 'var(--space-2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={labelStyle}>Sales (last {rangeLabel})</span>
          {avgPerDay > 0 && (
            <span style={{ fontSize: 10, color: 'var(--color-success-text)' }}>
              avg {formatMoney(avgPerDay)} / day
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 48, marginTop: 6 }}>
          {sparkline.map((v, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                background: v > 0 ? 'var(--color-primary)' : 'var(--color-surface-2)',
                borderRadius: '2px 2px 0 0',
                height: v > 0 ? `${Math.max(8, (v / maxBar) * 100)}%` : 4,
              }}
              title={v > 0 ? formatMoney(v) : 'no sales'}
            />
          ))}
        </div>
      </Card>
    </div>
  )
}

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 'var(--space-2)',
}

const labelStyle: CSSProperties = {
  fontSize: 10,
  color: 'var(--color-text-faint)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontWeight: 700,
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'default' | 'success' | 'danger' }) {
  const color =
    tone === 'success' ? 'var(--color-success-text)' :
    tone === 'danger' ? 'var(--color-danger-text)' :
                        'var(--color-text)'
  return (
    <Card padding="var(--space-3)">
      <div style={labelStyle}>{label}</div>
      <div style={{
        fontSize: 'var(--font-size-xl)',
        fontWeight: 800,
        marginTop: 2,
        fontVariantNumeric: 'tabular-nums',
        color,
      }}>
        {value}
      </div>
    </Card>
  )
}
